/**
 * Manages product enrichment fields and add-on products on the product page.
 * Works with product-form-component to validate fields and batch-add add-ons to cart.
 */
class ProductEnrichmentForm extends HTMLElement {
  connectedCallback() {
    this.#syncCheckboxFields();
    this.#bindCheckboxSync();
    this.#bindLiveClear();
    this.#bindAgreementGate();
    this.#updatePurchaseGate();
  }

  /**
   * Validates all required enrichment fields and agreement checkbox.
   * @returns {boolean}
   */
  validate() {
    let valid = true;

    this.querySelectorAll('[data-enrichment-field] [data-enrichment-required="true"]').forEach((field) => {
      const fieldEl = field.closest('[data-enrichment-field]');
      if (!fieldEl) return;

      const isEmpty = this.#isFieldEmpty(field);

      if (isEmpty) {
        fieldEl.classList.add('ef--has-error');
        const input = fieldEl.querySelector('.ef__input');
        if (input) input.classList.add('ef__input--error');
        valid = false;
      } else {
        fieldEl.classList.remove('ef--has-error');
        const input = fieldEl.querySelector('.ef__input');
        if (input) input.classList.remove('ef__input--error');
      }
    });

    const agreement = this.querySelector('[data-enrichment-agreement]');
    const agreementInput = this.querySelector('[data-enrichment-agreement-input]');

    if (agreement instanceof HTMLElement && agreementInput instanceof HTMLInputElement) {
      if (!agreementInput.checked) {
        agreement.classList.add('pe--has-error');
        valid = false;
      } else {
        agreement.classList.remove('pe--has-error');
      }
    }

    if (!valid) {
      const firstError =
        this.querySelector('.ef--has-error') || this.querySelector('.pe--has-error');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return valid;
  }

  /**
   * Re-initializes gates and checkbox state after DOM updates (e.g. quick add modal morph).
   */
  refresh() {
    this.#syncCheckboxFields();
    this.#updatePurchaseGate();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getRelatedProductForm() {
    const modal = this.closest('.quick-add-modal');
    if (modal) {
      return modal.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    }

    const details = this.closest('.product-details');
    if (details) {
      return details.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    }

    const productComponent = this.closest('product-component');
    if (productComponent) {
      const formInComponent = productComponent.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"]`
      );
      if (formInComponent) return formInComponent;
    }

    return document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  /**
   * @returns {boolean}
   */
  hasSelectedAddOns() {
    return this.querySelectorAll('[data-addon-variant-id]:checked').length > 0;
  }

  /**
   * @param {Record<string, string>} mainProperties
   * @returns {Array<{variantId: string, quantity: number, properties: Record<string, string>}>}
   */
  getSelectedAddOnItems(mainProperties) {
    /** @type {Array<{variantId: string, quantity: number, properties: Record<string, string>}>} */
    const items = [];
    const bookingProperty = this.dataset.bookingProperty || '_Booking';
    const bookingValue =
      mainProperties[bookingProperty] ||
      mainProperties['Name and Surname'] ||
      this.dataset.productTitle ||
      '';

    const copyPropertyNames = (this.dataset.addonCopyProperties || 'Name and Surname')
      .split('\n')
      .map((name) => name.trim())
      .filter(Boolean);

    this.querySelectorAll('[data-addon-variant-id]:checked').forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) return;

      const variantId = checkbox.dataset.addonVariantId;
      if (!variantId) return;

      /** @type {Record<string, string>} */
      const properties = {
        [bookingProperty]: bookingValue,
      };

      for (const propertyName of copyPropertyNames) {
        const value = mainProperties[propertyName];
        if (value) {
          properties[propertyName] = value;
        }
      }

      const addOnTitle = checkbox.dataset.addonTitle;
      if (addOnTitle) {
        properties['_Add-on for'] = this.dataset.productTitle || bookingValue;
      }

      items.push({
        variantId,
        quantity: 1,
        properties,
      });
    });

    return items;
  }

  /**
   * @param {HTMLElement} field
   * @returns {boolean}
   */
  #isFieldEmpty(field) {
    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      return !field.checked;
    }

    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
      return !field.value.trim();
    }

    return false;
  }

  #syncCheckboxFields() {
    this.querySelectorAll('[data-enrichment-checkbox]').forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) return;

      const hiddenId = checkbox.id ? `${checkbox.id}-value` : null;
      const hidden = hiddenId ? document.getElementById(hiddenId) : null;
      if (!(hidden instanceof HTMLInputElement)) return;

      const checkedVal = hidden.dataset.enrichmentCheckedValue || 'Yes';
      const uncheckedVal = hidden.dataset.enrichmentUncheckedValue || '';

      if (checkbox.checked) {
        hidden.value = checkedVal;
        hidden.disabled = false;
      } else {
        hidden.value = uncheckedVal;
        hidden.disabled = !uncheckedVal;
      }
    });
  }

  #bindCheckboxSync() {
    this.querySelectorAll('[data-enrichment-checkbox]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.#syncCheckboxFields());
    });
  }

  #bindLiveClear() {
    this.querySelectorAll('[data-enrichment-input], [data-enrichment-checkbox]').forEach((field) => {
      field.addEventListener('input', () => this.#clearFieldError(field));
      field.addEventListener('change', () => this.#clearFieldError(field));
    });
  }

  #bindAgreementGate() {
    const agreementInput = this.querySelector('[data-enrichment-agreement-input]');
    if (!(agreementInput instanceof HTMLInputElement)) return;

    agreementInput.addEventListener('change', () => {
      const agreement = this.querySelector('[data-enrichment-agreement]');
      agreement?.classList.remove('pe--has-error');
      this.#updatePurchaseGate();
    });
  }

  #updatePurchaseGate() {
    const agreementRequired = this.querySelector('[data-enrichment-agreement]') !== null;
    const agreementInput = this.querySelector('[data-enrichment-agreement-input]');
    const accepted =
      !agreementRequired ||
      (agreementInput instanceof HTMLInputElement && agreementInput.checked);

    const productForm = this.#getRelatedProductForm();

    productForm?.querySelector('.accelerated-checkout-block')?.toggleAttribute('hidden', !accepted);

    productForm?.querySelectorAll('button[name="add"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.disabled && button.hasAttribute('disabled')) return;

      if (agreementRequired && !accepted) {
        button.dataset.agreementLocked = 'true';
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.agreementLocked === 'true') {
        delete button.dataset.agreementLocked;
        button.removeAttribute('aria-disabled');
      }
    });
  }

  /** @param {Element} field */
  #clearFieldError(field) {
    const fieldEl = field.closest('[data-enrichment-field]');
    if (!fieldEl) return;

    fieldEl.classList.remove('ef--has-error');
    const input = fieldEl.querySelector('.ef__input');
    if (input) input.classList.remove('ef__input--error');
  }
}

if (!customElements.get('product-enrichment-form')) {
  customElements.define('product-enrichment-form', ProductEnrichmentForm);
}

/**
 * Finds the enrichment form scoped to a product form context (e.g. quick add modal).
 * @param {string | undefined} productId
 * @param {Element | null | undefined} productFormComponent
 * @returns {ProductEnrichmentForm | null}
 */
export function getProductEnrichmentForm(productId, productFormComponent) {
  if (!productId) return null;

  /** @type {Element | Document} */
  let scope = document;

  if (productFormComponent) {
    const modal = productFormComponent.closest('.quick-add-modal');
    const details = productFormComponent.closest('.product-details');
    const productComponent = productFormComponent.closest('product-component');

    scope = modal || details || productComponent || productFormComponent.parentElement || document;
  }

  const enrichment = scope.querySelector(`product-enrichment-form[data-product-id="${productId}"]`);
  return enrichment instanceof ProductEnrichmentForm ? enrichment : null;
}

/**
 * Validates enrichment fields and agreement checkbox for a product.
 * @param {string | undefined} productId
 * @param {Element | null | undefined} [productFormComponent]
 * @returns {boolean}
 */
export function validateProductAddRequirements(productId, productFormComponent) {
  const enrichment = getProductEnrichmentForm(productId, productFormComponent);
  if (!enrichment) return true;
  return enrichment.validate();
}

/**
 * Refreshes enrichment forms within a container after DOM morphs (quick add modal).
 * @param {ParentNode} [root]
 */
export function refreshProductEnrichmentForms(root = document) {
  root.querySelectorAll('product-enrichment-form').forEach((element) => {
    if (element instanceof ProductEnrichmentForm) {
      element.refresh();
    }
  });
}

/**
 * @param {FormData} formData
 * @returns {Record<string, string>}
 */
export function extractPropertiesFromFormData(formData) {
  /** @type {Record<string, string>} */
  const properties = {};

  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && value) {
      properties[match[1]] = value.toString();
    }
  }

  return properties;
}
